/*
 * An open chat tells the server someone is watching (so a turn ending here
 * isn't signalled) with a heartbeat while the page is visible and focused or
 * recently used, and marks the thread's signal seen once it's actually in
 * front of you.
 */
import { useEffect, useState } from "react";

import type { AgentScope } from "@/features/agents/types";

import { AttentionAPI, WATCH_HEARTBEAT_MS } from "../api";
import { useAgentAttention } from "../context/AgentAttentionProvider";

const visibleNow = (): boolean =>
  typeof document !== "undefined" && !document.hidden;

const attending = (): boolean =>
  visibleNow() && (document.hasFocus?.() ?? true);

/**
 * How long a pointer or key on an unfocused but visible page counts as
 * someone there (a second screen, a browser that keeps focus elsewhere).
 */
export const PRESENCE_MS = 120_000;
const PRESENCE_EVENTS = ["pointermove", "pointerdown", "keydown", "wheel"];

export const usePageAttending = (): boolean => {
  const [focused, setFocused] = useState(attending);
  const [visible, setVisible] = useState(visibleNow);
  const [recent, setRecent] = useState(false);
  useEffect(() => {
    let lapse: ReturnType<typeof setTimeout> | null = null;
    let lastReset = 0;
    const update = () => {
      setFocused(attending());
      setVisible(visibleNow());
    };
    const input = () => {
      const now = Date.now();
      if (now - lastReset < 5_000) return;
      lastReset = now;
      setRecent(true);
      if (lapse) clearTimeout(lapse);
      lapse = setTimeout(() => setRecent(false), PRESENCE_MS);
    };
    document.addEventListener("visibilitychange", update);
    window.addEventListener("focus", update);
    window.addEventListener("blur", update);
    for (const e of PRESENCE_EVENTS)
      window.addEventListener(e, input, { passive: true });
    return () => {
      document.removeEventListener("visibilitychange", update);
      window.removeEventListener("focus", update);
      window.removeEventListener("blur", update);
      for (const e of PRESENCE_EVENTS) window.removeEventListener(e, input);
      if (lapse) clearTimeout(lapse);
    };
  }, []);
  return focused || (visible && recent);
};

export function useThreadAttendance(
  scope: Pick<AgentScope, "kind" | "id"> | null,
  threadId: string | null
): void {
  const on = usePageAttending();
  const { items, markSeen } = useAgentAttention();
  const kind = scope?.kind;
  const id = scope?.id;
  const signalled = !!threadId && items.some((i) => i.thread_id === threadId);

  useEffect(() => {
    if (!on || !kind || !id || !threadId) return undefined;
    const beat = () =>
      void AttentionAPI.watch({ kind, id }, threadId).catch(() => undefined);
    beat();
    const t = setInterval(beat, WATCH_HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [on, kind, id, threadId]);

  useEffect(() => {
    if (on && signalled && threadId) markSeen(threadId);
  }, [on, signalled, threadId, markSeen]);
}
